/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, List, Text } from '@/shared/ui';
import { DETAIL_ACTION_SECONDARY } from '@/shared/ui/components/composed/detail-stat-strip/detail-action-classes';
import { cn } from '@/shared/ui/utils/tailwind';
import type { Visit } from '@/services/requests/housien-models/Visit';
import { formatVisitMoment } from '@/features/visits/utils/visit-timing';
import { DEAL_PANEL_CLASS, DealPanelLabel } from './DealPanel';

export interface DealVisitsPanelProps {
  agencyId: string;
  visits: Visit[];
  isLoading: boolean;
  /**
   * Settles one visit from its own row. Given the visit and the status
   * it should move to, so the panel never decides the transition itself.
   */
  onSettle: (visit: Visit, status: 'confirmed' | 'done') => void;
  /** Opens the booking drawer. The section owns visits, so it offers one. */
  onSchedule: () => void;
}

/**
 * The tone each visit status wears, keyed to the roles already defined
 * in `tokens.css` rather than to a palette of its own.
 */
const STATUS_TONE: Record<string, string> = {
  pending: 'var(--deal-warn)',
  confirmed: 'var(--seed-base)',
  done: 'var(--deal-won)',
  cancelled: 'var(--deal-lost)',
  no_show: 'var(--status-error-text)',
};

/**
 * Every visit booked under this attendance, newest first.
 *
 * The screen could book a visit and never show one back, so a broker had
 * to leave for the Visitas board to find out whether the visit they just
 * scheduled still stood -- and the stage, which keeps saying "Visita
 * Agendada" through `pending`, `confirmed` and `done` alike, could not
 * tell them.
 *
 * Each row links to the visit on that board, which owns rescheduling,
 * cancelling and the participant list. This panel deliberately does not:
 * a second place to edit a visit is a second place for the two to
 * disagree.
 *
 * @param props.agencyId - The owning agency, for the links.
 * @param props.visits - The attendance's visits.
 * @param props.isLoading - Whether they are still being fetched.
 * @returns The panel.
 */
export function DealVisitsPanel({
  agencyId, visits, isLoading, onSettle, onSchedule,
}: DealVisitsPanelProps) {
  const { t } = useTranslation('features/deals');

  const ordered = [...visits].sort((a, b) => {
    const left = a.visit_datetime ?? '';
    const right = b.visit_datetime ?? '';
    return left > right ? -1 : 1;
  });

  return (
    <div className={cn(DEAL_PANEL_CLASS, 'gap-(--space-3)')} data-testid="deal-visits-panel">
      <div className="flex items-baseline justify-between gap-(--space-2)">
        <DealPanelLabel>{t('detail.visits.title')}</DealPanelLabel>
        {/*
          Booking lives here rather than in the action bar. The section
          owns the attendance's visits, and the bar was trimmed to the
          three actions that hold or end the attendance.
        */}
        <Button
          size="custom"
          variant="none"
          data-testid="deal-visits-schedule"
          className={cn(DETAIL_ACTION_SECONDARY, 'px-(--space-3) py-(--space-1)')}
          onClick={onSchedule}
        >
          {t('detail.visits.schedule')}
        </Button>
      </div>

      {isLoading && (
        <Text component="span" variant="bodySm" c="var(--text-tertiary)">
          {t('detail.visits.loading')}
        </Text>
      )}

      {!isLoading && ordered.length === 0 && (
        <Text component="span" variant="bodySm" c="var(--text-tertiary-soft)" className="italic">
          {t('detail.visits.empty')}
        </Text>
      )}

      {!isLoading && ordered.length > 0 && (
        <List className="flex list-none flex-col gap-(--space-2) p-0 m-0">
          {ordered.map((visit) => (
            <List.Item
              key={visit.visit_id}
              data-testid="deal-visit-row"
              data-status={visit.status}
              className={cn(
                'flex flex-wrap items-baseline justify-between gap-(--space-2)',
                'rounded-(--radius-md) border border-(--border-subtle)',
                'bg-(--surface-primary) px-(--space-3) py-(--space-2)',
              )}
            >
              <div className="flex min-w-0 flex-col gap-(--space-0-5)">
                <Text
                  component="span"
                  variant="bodySm"
                  c="var(--text-primary)"
                  className="[font-weight:var(--font-medium)]"
                >
                  {visit.visit_datetime
                    ? formatVisitMoment(visit.visit_datetime)
                    : t('detail.facts.undefined')}
                </Text>
                <Text
                  component="span"
                  variant="meta"
                  style={{ color: STATUS_TONE[visit.status ?? ''] ?? 'var(--text-tertiary)' }}
                  className="[font-weight:var(--font-semibold)]"
                >
                  {t(`detail.visits.status.${visit.status}`)}
                </Text>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-(--space-3)">
                {/*
                  The row acts on its own visit. The bar above only ever
                  reaches the soonest outstanding one, which left every
                  other booking on this attendance readable and not
                  actionable.
                */}
                {visit.status === 'pending' && (
                  <Button
                    size="custom"
                    variant="none"
                    data-testid="deal-visit-confirm"
                    className={cn(DETAIL_ACTION_SECONDARY, 'px-(--space-3) py-(--space-1)')}
                    onClick={() => onSettle(visit, 'confirmed')}
                  >
                    {t('detail.actions.confirmVisit')}
                  </Button>
                )}
                {visit.status === 'confirmed' && (
                  <Button
                    size="custom"
                    variant="none"
                    data-testid="deal-visit-complete"
                    className={cn(DETAIL_ACTION_SECONDARY, 'px-(--space-3) py-(--space-1)')}
                    onClick={() => onSettle(visit, 'done')}
                  >
                    {t('detail.visits.markDone')}
                  </Button>
                )}
                {/*
                  `?visit=` is the param the visits page reads to open its
                  detail overlay -- the `visits/:visit_id` route redirects
                  to the list, so a path link would land on the board with
                  nothing open.
                */}
                <Link
                  to={`/app/agencies/${agencyId}/visits?visit=${visit.visit_id}`}
                  data-testid="deal-visit-link"
                  className="text-(--seed-base) underline-offset-2 hover:underline"
                  style={{ fontSize: 'var(--text-meta)' }}
                >
                  {t('detail.visits.openDetail')}
                </Link>
              </div>
            </List.Item>
          ))}
        </List>
      )}

      <Link
        to={`/app/agencies/${agencyId}/visits`}
        data-testid="deal-visits-all-link"
        className="self-start text-(--seed-base) underline-offset-2 hover:underline"
        style={{ fontSize: 'var(--text-meta)' }}
      >
        {t('detail.visits.seeAll')}
      </Link>
    </div>
  );
}

export default DealVisitsPanel;
